import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { BoxIcon, CircleDotIcon, GaugeIcon, LayersIcon, PackageIcon, PrinterIcon, TagIcon, TargetIcon, UserIcon } from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { PURPOSES, STATUSES, useBatches } from '@/api/batches'
import type { Batch, BatchFilters } from '@/api/batches'
import { useMachines } from '@/api/machines'
import { useModels } from '@/api/models'
import { useProducts } from '@/api/products'
import { useWorkspace } from '@/api/workspace'
import { versionLabel } from '@/components/batches/batch-format'
import { BatchStatusBadge } from '@/components/batches/batch-parts'
import { VoidedBadge } from '@/components/corrections/voided'
import { CsvExportButton } from '@/components/csv-export/csv-export-button'
import { FilterBar } from '@/components/filter-bar'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { RecordLink, RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import { Button } from '@/components/ui/button'

// Batches list (UI-SCREENS F-04, #128): number, Machine, Model version, Variant, purpose, status,
// plates, OK / failed once completed (04c), the cancel reason once cancelled (04e), Yield on a
// completed, non-voided production Batch (04j #136), date and created by, filtered by status,
// Machine, Product, Model, purpose and date range. Owner and Staff both see it and both start
// Batches. The Owner downloads the filtered list as CSV (#137).
//
// The filters live in the URL (#144), so a filtered list survives reload and can be linked to (F-03
// "Riwayat batch cetak" opens `?machineId=`). Each param is optional and a missing, empty or invalid
// one is dropped (`catch`), never an error, and so is a `machineId` no Machine has. They sit in the
// shared Linear-style filter bar (as on I-03). Picking a value pushes a history entry, so Back
// restores the previous filter set; a date range commits with `replace`. Clearing a chip removes
// the param. New filters go in `searchSchema` and `toFilters`. The Model filter lists the chosen
// Product's Models and shows once a Product is picked (04j #136): changing the Product clears
// `modelId`, and a `modelId` without a Product, or not of that Product, is dropped.
const dateSchema = z.iso.date()

const searchSchema = z.object({
  status: z.enum(STATUSES).optional().catch(undefined),
  machineId: z.coerce.number().int().positive().optional().catch(undefined),
  productId: z.coerce.number().int().positive().optional().catch(undefined),
  modelId: z.coerce.number().int().positive().optional().catch(undefined),
  purpose: z.enum(PURPOSES).optional().catch(undefined),
  from: dateSchema.optional().catch(undefined),
  to: dateSchema.optional().catch(undefined),
})

type BatchSearch = z.infer<typeof searchSchema>

const CLEARED: BatchSearch = {
  status: undefined,
  machineId: undefined,
  productId: undefined,
  modelId: undefined,
  purpose: undefined,
  from: undefined,
  to: undefined,
}

const toId = (value: string | undefined) => (value === undefined ? undefined : Number(value))

export const Route = createFileRoute('/w/$slug/batches/')({
  component: BatchesList,
  validateSearch: searchSchema,
  staticData: { breadcrumb: 'batches.title' },
})

// The URL params as the index query (`machine_id`, `product_id`, `model_id` on the API), without
// the unset ones.
function toFilters({ status, machineId, productId, modelId, purpose, from, to }: BatchSearch): BatchFilters {
  return {
    ...(status ? { status } : {}),
    ...(machineId ? { machine_id: machineId } : {}),
    ...(productId ? { product_id: productId } : {}),
    ...(modelId ? { model_id: modelId } : {}),
    ...(purpose ? { purpose } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  }
}

function BatchesList() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const machines = useMachines(slug, { archived: true })
  const products = useProducts(slug, { archived: true })
  const models = useModels(slug, search.productId ?? 0, { enabled: search.productId !== undefined })
  const filters = toFilters(search)
  const setFilter = (patch: Partial<BatchSearch>, replace = false) =>
    navigate({ search: (prev) => searchSchema.parse({ ...prev, ...patch }), replace, resetScroll: false })
  // A `machineId` no Machine has (archived ones included) is dropped once the Machines load, so the
  // select and the list agree.
  const unknownMachine =
    search.machineId !== undefined && machines.data !== undefined && !machines.data.some((m) => m.id === search.machineId)
  useEffect(() => {
    if (unknownMachine) {
      navigate({ search: (prev) => ({ ...prev, machineId: undefined }), replace: true, resetScroll: false })
    }
  }, [unknownMachine, navigate])
  // The same for a `productId` no Product has (archived ones included), and for a `modelId`
  // without a Product or not among its Models; an unknown Product drops its Model too.
  const unknownProduct =
    search.productId !== undefined && products.data !== undefined && !products.data.some((p) => p.id === search.productId)
  const unknownModel =
    search.modelId !== undefined &&
    (search.productId === undefined || unknownProduct || (models.data !== undefined && !models.data.some((m) => m.id === search.modelId)))
  useEffect(() => {
    if (unknownProduct || unknownModel) {
      navigate({
        search: (prev) => ({ ...prev, ...(unknownProduct ? { productId: undefined } : {}), modelId: undefined }),
        replace: true,
        resetScroll: false,
      })
    }
  }, [unknownProduct, unknownModel, navigate])
  const batches = useBatches(slug, filters)
  const isOwner = useWorkspace(slug).data?.role === 'owner'
  const filtered = Object.keys(filters).length > 0

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-semibold">{t('batches.title')}</h1>
        <div className="flex flex-wrap gap-2">
          {isOwner ? <CsvExportButton slug={slug} list="batches" query={filters} /> : null}
          <Button asChild className="h-11">
            <Link to="/w/$slug/batches/new" params={{ slug }}>
              {t('batches.start')}
            </Link>
          </Button>
        </div>
      </div>

      <FilterBar
        fields={[
          {
            key: 'status',
            label: t('batches.columns.status'),
            icon: CircleDotIcon,
            value: search.status,
            options: STATUSES.map((value) => ({ value, label: t(`batches.status.${value}`) })),
            onChange: (value) => setFilter({ status: value as BatchSearch['status'] }),
          },
          {
            key: 'machineId',
            label: t('batches.columns.machine'),
            icon: PrinterIcon,
            value: search.machineId ? String(search.machineId) : undefined,
            options: (machines.data ?? []).map((machine) => ({ value: String(machine.id), label: machine.name })),
            onChange: (value) => setFilter({ machineId: toId(value) }),
          },
          {
            key: 'productId',
            label: t('batches.columns.product'),
            icon: PackageIcon,
            value: search.productId ? String(search.productId) : undefined,
            options: (products.data ?? []).map((product) => ({
              value: String(product.id),
              label: `${product.code} ${product.name}`,
            })),
            onChange: (value) => setFilter({ productId: toId(value), modelId: undefined }),
          },
          // The Model is offered once a Product is picked: it lists that Product's Models.
          ...(search.productId
            ? [
                {
                  key: 'modelId',
                  label: t('batches.columns.model'),
                  icon: BoxIcon,
                  value: search.modelId ? String(search.modelId) : undefined,
                  options: (models.data ?? []).map((model) => ({ value: String(model.id), label: model.name })),
                  onChange: (value: string | undefined) => setFilter({ modelId: toId(value) }),
                },
              ]
            : []),
          {
            key: 'purpose',
            label: t('batches.columns.purpose'),
            icon: TargetIcon,
            value: search.purpose,
            options: PURPOSES.map((value) => ({ value, label: t(`batches.purpose.${value}`) })),
            onChange: (value) => setFilter({ purpose: value as BatchSearch['purpose'] }),
          },
        ]}
        date={{ from: search.from, to: search.to }}
        onDateChange={(range) => setFilter({ from: range.from, to: range.to }, true)}
        onClearAll={() => setFilter(CLEARED)}
      />

      {batches.isPending ? (
        <LoadingSkeleton />
      ) : batches.isError || !batches.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : batches.data.length === 0 ? (
        <RecordListEmpty>{t(filtered ? 'batches.emptyFiltered' : 'batches.empty')}</RecordListEmpty>
      ) : (
        <RecordList>
          {batches.data.map((batch) => (
            <BatchRow key={batch.id} slug={slug} batch={batch} />
          ))}
        </RecordList>
      )}
    </section>
  )
}

function BatchRow({ slug, batch }: { slug: string; batch: Batch }) {
  const { t } = useTranslation()
  const completed = batch.status === 'completed'

  return (
    <RecordListItem
      voided={Boolean(batch.voided_at)}
      title={
        <RecordLink to="/w/$slug/batches/$batchId" params={{ slug, batchId: String(batch.id) }}>
          {batch.batch_no}
        </RecordLink>
      }
      badges={
        <>
          <BatchStatusBadge batch={batch} />
          <VoidedBadge record={batch} />
        </>
      }
      meta={[
        { icon: PrinterIcon, label: t('batches.columns.machine'), value: batch.machine.name },
        { icon: BoxIcon, label: t('batches.columns.modelVersion'), value: versionLabel(t, batch) },
        batch.variant ? { icon: TagIcon, label: t('batches.columns.variant'), value: batch.variant.sku } : null,
        { icon: TargetIcon, label: t('batches.columns.purpose'), value: t(`batches.purpose.${batch.purpose}`) },
        { icon: LayersIcon, label: t('batches.columns.plates'), value: t('batches.list.plates', { count: batch.plates }) },
        batch.yield_percent !== null
          ? { icon: GaugeIcon, label: t('batches.columns.yield'), value: t('batches.list.yield', { percent: batch.yield_percent }) }
          : null,
        batch.created_by
          ? { icon: UserIcon, label: t('batches.columns.createdBy'), value: batch.created_by.name ?? batch.created_by.email }
          : null,
      ]}
      description={
        batch.status === 'cancelled' && batch.cancel_reason
          ? t('batches.list.cancelled', { reason: t(`batches.cancelReason.${batch.cancel_reason}`) })
          : undefined
      }
      figure={
        completed ? t('batches.list.parts', { ok: batch.parts_ok ?? 0, failed: batch.parts_failed ?? 0 }) : undefined
      }
      caption={formatBusinessDate(batch.occurred_on)}
    />
  )
}
